import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Eraser, Check, Loader2 } from "lucide-react";

/**
 * Minimal canvas-based signature pad.
 * - Only calls onSave(dataUrl) with a real, non-empty signature.
 * - Empty / failed captures surface an inline error and keep the pad open
 *   (never a silent close), so required-signature forms can't be blocked by
 *   an invisible failure.
 * - Re-measures on resize / rotation while preserving the current drawing.
 */
export function SignaturePad({ onSave, onCancel, testIdPrefix = "sig", busy = false }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef(null);
  const [empty, setEmpty] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    let raf = null;

    const paintBackground = (ctx, w, h) => {
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#09090B";
      ctx.lineWidth = 2.2;
      ctx.fillStyle = "#ffffff"; // opaque white so the PNG isn't transparent
      ctx.fillRect(0, 0, w, h);
    };

    // Measure the canvas against its laid-out size. When `preserve` is true we
    // keep whatever has already been drawn (used on resize / rotation) so a
    // valid signature is never wiped by a viewport change.
    const measure = (preserve) => {
      const rect = c.getBoundingClientRect();
      if (!rect.width || !rect.height) return false;
      const ratio = window.devicePixelRatio || 1;
      let snapshot = null;
      if (preserve && c.width && c.height) {
        try { snapshot = c.toDataURL("image/png"); } catch { snapshot = null; }
      }
      c.width = Math.max(1, Math.round(rect.width * ratio));
      c.height = Math.max(1, Math.round(rect.height * ratio));
      const ctx = c.getContext("2d");
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(ratio, ratio);
      paintBackground(ctx, rect.width, rect.height);
      if (snapshot) {
        const img = new Image();
        img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
        img.src = snapshot;
      }
      return true;
    };

    // Dialog open animation can report 0×0 on iOS — retry until laid out.
    let tries = 0;
    const init = () => {
      if (!measure(false) && tries < 60) {
        tries += 1;
        raf = requestAnimationFrame(init);
      }
    };
    init();

    const onResize = () => measure(true);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  const pointer = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    if (error) setError("");
    drawingRef.current = true;
    lastRef.current = pointer(e);
    setEmpty(false);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault(); // also stops the page scrolling while signing
    const ctx = canvasRef.current.getContext("2d");
    const p = pointer(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
  };

  const end = () => { drawingRef.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setEmpty(true);
    setError("");
  };

  const save = () => {
    if (empty) {
      setError("Please draw your signature in the box before saving.");
      return;
    }
    let dataUrl = null;
    try {
      dataUrl = canvasRef.current.toDataURL("image/png");
    } catch {
      dataUrl = null; // Safari throws on zero-size / tainted canvas
    }
    if (!dataUrl || dataUrl === "data:,") {
      setError("Your signature couldn't be captured on this device. Please draw it again.");
      return;
    }
    setError("");
    onSave?.(dataUrl);
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-pad`}>
      <div
        className="border-2 border-zinc-300 bg-white"
        style={{ touchAction: "none", overscrollBehavior: "contain" }}
      >
        <canvas
          ref={canvasRef}
          className="block w-full h-40 cursor-crosshair"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          onTouchCancel={end}
          data-testid={`${testIdPrefix}-canvas`}
        />
      </div>
      {error && (
        <p className="text-sm text-red-600 font-medium" role="alert" data-testid={`${testIdPrefix}-error`}>
          {error}
        </p>
      )}
      <div className="flex items-center justify-between gap-2">
        <Button type="button" variant="outline" size="sm" onClick={clear} disabled={busy}
          className="rounded-sm uppercase tracking-wider font-bold text-xs"
          data-testid={`${testIdPrefix}-clear`}>
          <Eraser className="w-3.5 h-3.5 mr-1.5" /> Clear
        </Button>
        <div className="flex gap-2">
          {onCancel && (
            <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={busy}
              className="rounded-sm uppercase tracking-wider font-bold text-xs"
              data-testid={`${testIdPrefix}-cancel`}>
              Cancel
            </Button>
          )}
          <Button type="button" size="sm" onClick={save} disabled={busy}
            className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold text-xs"
            data-testid={`${testIdPrefix}-save`}>
            {busy
              ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…</>
              : <><Check className="w-3.5 h-3.5 mr-1.5" /> Save signature</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
